import React, { useEffect, useMemo, useState } from "react";
import { WebMascot } from "../components/WebMascot";
import { ChildWithProgress } from "../WebShell";
import { childrenApi, ChildActivity, ActivityRange } from "../../api/children";
import { useAuthStore } from "../../state/authStore";

// Activity / Dashboard — backed by /v1/children/:childId/activity.
//
// Every number here is DB-derived from real sessions — nothing is mocked.
// The Day / Week / Month tabs each re-fetch with a matching `range` param;
// the backend windows to 1 / 7 / 30 days (see ProgressService.getActivity).
// Note: the four headline stat cards are today/all-time KPIs and do NOT
// re-window with the tabs — only the chart, breakdown, and recent list do.

const DAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

// Stable color palette for the category bars. Keyed by lowercase category
// name so the same topic gets the same color across renders. Unknown
// categories fall back to the primary orange.
const CAT_COLOR: Record<string, string> = {
  numbers: "#FF8A4C",
  letters: "#5B8DEF",
  colors: "#F18EB6",
  shapes: "#7AC74F",
  "social skills": "#8C7DD9",
  language: "#A07248",
  bedtime: "#3D3A75",
  creativity: "#FFD86B",
};

function titleCase(s: string): string {
  return s
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

function formatRelative(iso: string): string {
  const now = new Date();
  const d = new Date(iso);
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  const time = d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  if (d >= startOfToday) return `Today, ${time}`;
  if (d >= startOfYesterday) return `Yesterday, ${time}`;
  // Older: show day name + time
  return `${d.toLocaleDateString(undefined, { weekday: "short" })}, ${time}`;
}

export function WebActivity({ child }: { child: ChildWithProgress }) {
  const { accessToken } = useAuthStore();
  const theme = child.theme ?? { color: "#FF8A4C", bg: "#FFE2C2" };

  const [activity, setActivity] = useState<ChildActivity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Active tab — drives the `range` query param + the chart label below.
  // Persisted locally per browser so the parent's choice survives reloads.
  const [range, setRange] = useState<ActivityRange>(() => {
    if (typeof window === "undefined") return "week";
    const saved = window.localStorage.getItem("mapa.activity.range");
    return saved === "day" || saved === "month" ? saved : "week";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("mapa.activity.range", range);
    }
  }, [range]);

  useEffect(() => {
    if (!accessToken || !child.id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    childrenApi
      .getActivity(child.id, accessToken, range)
      .then((data) => {
        if (!cancelled) setActivity(data);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : "Couldn't load activity.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, child.id, range]);

  // Derived metrics from the live progress payload that's already on the
  // ChildWithProgress prop (loaded by WebShell). Avoids a second fetch.
  const todayMin = child.progress
    ? Math.round(child.progress.todayWatchedSeconds / 60)
    : 0;
  const stars = child.progress?.correctAnswers ?? 0;
  const accuracyRaw = child.progress?.accuracy ?? 0;
  const accuracy =
    accuracyRaw <= 1 ? Math.round(accuracyRaw * 100) : Math.round(accuracyRaw);

  // Weekly chart data — map to minutes and pick the visual highlight as
  // today's column (Monday-indexed). Falls back to all-zero if the
  // activity fetch hasn't returned yet.
  const week = activity?.weekly.map((w) =>
    Math.round(w.secondsWatched / 60),
  ) ?? Array.from({ length: 7 }, () => 0);
  const todayIdx = useMemo(() => {
    // JS Date.getDay(): Sun=0..Sat=6. Convert to Mon=0..Sun=6 like the chart.
    const js = new Date().getDay();
    return (js + 6) % 7;
  }, []);
  const maxWeek = Math.max(1, ...week);
  const weekMinutes = activity?.weekMinutes ?? week.reduce((a, b) => a + b, 0);

  // True session presence in the window — drives the empty states. We key
  // off `recent` (the windowed session list) rather than rounded minutes,
  // so a few sub-minute sessions don't read as "no sessions" while the
  // category panel right beside it shows data from those very sessions.
  const hasSessions = (activity?.recent.length ?? 0) > 0;
  const windowSeconds =
    activity?.weekly.reduce((a, w) => a + w.secondsWatched, 0) ?? 0;
  // Sub-minute activity rounds to 0 min — say "under 1 min" so the header
  // never claims "0 min" next to a populated breakdown.
  const minutesLabel =
    weekMinutes === 0 && windowSeconds > 0 ? "under 1 min" : `${weekMinutes} min`;
  // Period word that matches the active range, so empty-state copy reads
  // correctly in Day / Month view (not always "this week").
  const periodWord =
    range === "day"
      ? "today"
      : range === "month"
        ? "in the last 30 days"
        : "this week";

  return (
    <div className="fadeup w-resppad" style={{ padding: 32 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: theme.bg,
            overflow: "hidden",
            display: "grid",
            placeItems: "center",
          }}
        >
          <div style={{ marginTop: 10 }}>
            <WebMascot mood="happy" color={theme.color} size={48} />
          </div>
        </div>
        <div>
          <div
            style={{
              fontFamily: "var(--display)",
              fontSize: 28,
              fontWeight: 900,
              color: "var(--ink)",
            }}
          >
            {child.name}&apos;s activity
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-3)" }}>
            {range === "day"
              ? "Today"
              : range === "month"
                ? "Last 30 days"
                : "This week"}
          </div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          {/* Day / Week / Month tabs — each one re-fetches /activity with
              the matching range query param. Active state mirrors the
              `range` state; localStorage persists the parent's choice. */}
          <div className="w-tabs">
            {(["day", "week", "month"] as ActivityRange[]).map((r) => (
              <button
                key={r}
                className={"w-tab " + (range === r ? "active" : "")}
                onClick={() => setRange(r)}
                type="button"
              >
                {r === "day" ? "Day" : r === "week" ? "Week" : "Month"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div
        className="w-respgrid2"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 14,
          marginBottom: 24,
        }}
      >
        <div className="w-stat">
          <div className="label">Minutes today</div>
          <div className="value">{todayMin}</div>
          <div className="delta">
            of {child.controls?.dailyMinutes ?? 60} min/day
          </div>
        </div>
        <div className="w-stat">
          <div className="label">Stars earned</div>
          <div className="value">{stars}</div>
          <div className="delta">all-time</div>
        </div>
        <div className="w-stat">
          <div className="label">Question accuracy</div>
          <div className="value">{accuracy}%</div>
          <div className="delta" style={{ color: "var(--ink-3)" }}>
            {accuracy >= 75
              ? "keep it up"
              : accuracy >= 50
                ? "getting there"
                : "early days"}
          </div>
        </div>
        <div className="w-stat">
          <div className="label">Streak</div>
          <div className="value">
            {activity?.streakDays ?? 0}{" "}
            {activity?.streakDays === 1 ? "day" : "days"}
          </div>
          <div className="delta">
            {(activity?.streakDays ?? 0) >= 3 ? "🔥 keep going" : "build it"}
          </div>
        </div>
      </div>

      <div
        className="w-respgrid"
        style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 18 }}
      >
        <div className="w-panel">
          <h3>
            {range === "day"
              ? "Time watched today"
              : range === "month"
                ? "Time watched · last 30 days"
                : "Time watched this week"}{" "}
            · {minutesLabel}
          </h3>
          {loading ? (
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "var(--ink-3)",
                padding: 12,
              }}
            >
              Loading…
            </div>
          ) : !hasSessions ? (
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "var(--ink-3)",
                padding: 12,
              }}
            >
              No sessions yet {periodWord}.
            </div>
          ) : weekMinutes === 0 ? (
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "var(--ink-3)",
                padding: 12,
              }}
            >
              Less than a minute watched {periodWord} — the chart fills in
              once sessions add up to a minute or more.
            </div>
          ) : (
            <div className="w-bars">
              {week.map((v, i) => (
                <div key={i} className="w-bar">
                  <div
                    className={"col " + (i === todayIdx ? "" : "dim")}
                    style={{ height: `${(v / maxWeek) * 100}%` }}
                  >
                    {i === todayIdx && v > 0 && (
                      <span className="cap">{v}m</span>
                    )}
                  </div>
                  <div className="lbl">{DAY_LABELS[i]}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="w-panel">
          <h3>What {child.name} learned</h3>
          {loading ? (
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "var(--ink-3)",
                padding: 12,
              }}
            >
              Loading…
            </div>
          ) : !activity || activity.categories.length === 0 ? (
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "var(--ink-3)",
                padding: 12,
              }}
            >
              No category data yet — categories appear after the first few
              sessions.
            </div>
          ) : (
            <div
              style={{ display: "flex", flexDirection: "column", gap: 12 }}
            >
              {activity.categories.map((c) => {
                const col = CAT_COLOR[c.name.toLowerCase()] ?? "#FF8A4C";
                return (
                  <div key={c.name}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 13,
                        fontWeight: 800,
                        color: "var(--ink)",
                        marginBottom: 6,
                      }}
                    >
                      <span>{titleCase(c.name)}</span>
                      <span style={{ color: "var(--ink-2)" }}>{c.pct}%</span>
                    </div>
                    <div
                      style={{
                        height: 8,
                        borderRadius: 8,
                        background: "var(--line)",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${Math.min(c.pct, 100)}%`,
                          height: "100%",
                          background: col,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="w-panel" style={{ marginTop: 18 }}>
        <h3>Recent activity</h3>
        {loading ? (
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: "var(--ink-3)",
              padding: 12,
            }}
          >
            Loading…
          </div>
        ) : error ? (
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "#C0392B",
              padding: 12,
            }}
          >
            {error}
          </div>
        ) : !activity || activity.recent.length === 0 ? (
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "var(--ink-3)",
              padding: 12,
            }}
          >
            Nothing yet — {child.name}&apos;s viewing history will show up
            here as soon as they watch their first video.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {activity.recent.map((r, i) => (
              <div key={i} className="w-row" style={{ padding: "12px 0" }}>
                <div
                  style={{ display: "flex", gap: 14, alignItems: "center" }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "var(--ink-3)",
                      width: 160,
                    }}
                  >
                    {formatRelative(r.at)}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "var(--ink-2)",
                    }}
                  >
                    Watched
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 800,
                      color: "var(--ink)",
                    }}
                  >
                    {r.videoTitle}
                  </div>
                </div>
                <div
                  style={{ display: "flex", gap: 14, alignItems: "center" }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "var(--ink-3)",
                    }}
                  >
                    {r.minutes} min
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 800,
                      color: "var(--primary-ink)",
                      minWidth: 70,
                      textAlign: "right",
                    }}
                  >
                    {r.stars > 0 ? `⭐ ${r.stars}` : "—"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default WebActivity;
